import { FolderKanban } from 'lucide-react';
import { Link } from '@tanstack/react-router';
import type { MyProjectEntry } from '@hygge/shared';
import { Skeleton } from '@/components/ui/skeleton';
import { useMyProjects } from '@/hooks/use-dashboard';
import { WidgetCard } from '../widget-card';
import { ExpandableList } from '../expandable-list';

function ProjectRow({ p }: { p: MyProjectEntry }) {
  return (
    <Link
      to="/projects/$id"
      params={{ id: p.id }}
      className="dashboard-row group flex items-center gap-3 border-b border-border py-2.5 last:border-0"
    >
      <div className="min-w-0 flex-1 truncate text-sm font-medium group-hover:underline">
        {p.name}
      </div>
    </Link>
  );
}

/**
 * `myProjects` widget — the signed-in user's ACTIVE projects where they are a
 * project manager (`ProjectMember.isManager`). Each row links to the project. No health
 * status is shown: the widget is a plain list of the projects you manage.
 */
export function MyProjectsWidget() {
  const { data, isLoading, isError } = useMyProjects();
  const rows = data ?? [];

  return (
    <WidgetCard
      title="My Projects"
      icon={FolderKanban}
      caption="Active projects you manage."
    >
      {isLoading ? (
        <div className="space-y-3">
          {Array.from({ length: 3 }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="h-4 w-40" />
            </div>
          ))}
        </div>
      ) : isError ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          Couldn&apos;t load your projects.
        </p>
      ) : rows.length === 0 ? (
        <p className="py-4 text-center text-xs text-muted-foreground">
          You don&apos;t manage any active projects.
        </p>
      ) : (
        <ExpandableList
          items={rows}
          getKey={(p) => p.id}
          renderItem={(p: MyProjectEntry) => <ProjectRow p={p} />}
        />
      )}
    </WidgetCard>
  );
}
